<script setup lang="ts">
import type { HTMLAttributes } from 'vue'
import { FaTag } from '@fantastic-admin/components'
import { computed } from 'vue'

type AppStatusTone = 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'info'
type AppStatusAppearance = 'soft' | 'solid' | 'outline'
type AppStatusSize = 'small' | 'default'
type FaTagVariant = 'default' | 'secondary' | 'destructive' | 'outline'

interface AppStatusOption {
  label: string
  tone?: AppStatusTone
  icon?: string
}

defineOptions({
  name: 'AppStatusTag',
})

const props = withDefaults(defineProps<{
  value?: string | number | boolean | null
  label?: string
  tone?: AppStatusTone
  options?: Record<string, AppStatusOption>
  emptyText?: string
  appearance?: AppStatusAppearance
  size?: AppStatusSize
  icon?: string
  class?: HTMLAttributes['class']
}>(), {
  value: null,
  label: '',
  tone: 'default',
  options: () => ({}),
  emptyText: '-',
  appearance: 'soft',
  size: 'default',
  icon: '',
})

// 原生状态直接复用 FaTag variant，新增状态只补充主题语义色，不维护独立硬编码色板。
const toneVariantMap: Record<AppStatusAppearance, Record<AppStatusTone, FaTagVariant>> = {
  soft: {
    default: 'secondary',
    primary: 'secondary',
    success: 'secondary',
    warning: 'secondary',
    danger: 'secondary',
    info: 'secondary',
  },
  solid: {
    default: 'secondary',
    primary: 'default',
    success: 'secondary',
    warning: 'secondary',
    danger: 'destructive',
    info: 'secondary',
  },
  outline: {
    default: 'outline',
    primary: 'outline',
    success: 'outline',
    warning: 'outline',
    danger: 'outline',
    info: 'outline',
  },
}

const toneStyleMap: Record<AppStatusAppearance, Record<AppStatusTone, string>> = {
  soft: {
    default: '',
    primary: 'bg-primary/10 text-primary dark:bg-primary/15',
    success: 'bg-success/12 text-success dark:bg-success/18',
    warning: 'bg-warning/14 text-warning dark:bg-warning/18',
    danger: 'bg-destructive/10 text-destructive dark:bg-destructive/20',
    info: 'bg-info/12 text-info dark:bg-info/18',
  },
  solid: {
    default: '',
    primary: '',
    success: 'border-transparent bg-success text-success-foreground',
    warning: 'border-transparent bg-warning text-warning-foreground',
    danger: '',
    info: 'border-transparent bg-info text-info-foreground',
  },
  outline: {
    default: '',
    primary: 'border-primary/35 text-primary',
    success: 'border-success/35 text-success',
    warning: 'border-warning/40 text-warning',
    danger: 'border-destructive/35 text-destructive',
    info: 'border-info/35 text-info',
  },
}

const sizeStyleMap: Record<AppStatusSize, string> = {
  small: 'h-5 px-2',
  default: '',
}

const resolvedStatus = computed(() => {
  if (props.label) {
    return {
      label: props.label,
      tone: props.tone,
      icon: props.icon,
    }
  }

  const option = props.options[String(props.value)]
  if (option) {
    return {
      label: option.label,
      tone: option.tone ?? props.tone,
      icon: option.icon ?? props.icon,
    }
  }

  if (props.value === null || props.value === undefined || props.value === '') {
    return {
      label: props.emptyText,
      tone: 'default' as const,
      icon: props.icon,
    }
  }

  return {
    label: String(props.value),
    tone: props.tone,
    icon: props.icon,
  }
})

const tagClass = computed(() => {
  return [
    'max-w-full min-w-0 shrink-0 rounded-full whitespace-nowrap',
    sizeStyleMap[props.size],
    toneStyleMap[props.appearance][resolvedStatus.value.tone],
    props.class,
  ]
})

const tagVariant = computed(() => {
  return toneVariantMap[props.appearance][resolvedStatus.value.tone]
})
</script>

<template>
  <FaTag
    :variant="tagVariant"
    :icon="resolvedStatus.icon"
    :class="tagClass"
    :title="resolvedStatus.label"
  >
    <span class="truncate">{{ resolvedStatus.label }}</span>
  </FaTag>
</template>
